import { Button, Dropdown, Modal, Tag } from 'antd'
import type { MenuProps } from 'antd'
import {
  CheckOutlined,
  DownOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  SettingOutlined,
  StarFilled,
} from '@ant-design/icons'
import type { AgentBuilderDashboardView } from '../types'
import { MAX_SAVED_VIEWS_PER_DASHBOARD } from '../viewer/savedViews'

const DEFAULT_KEY = '__dashboard_default__'
const SAVE_KEY = '__save__'
const UPDATE_KEY = '__update__'
const MANAGE_KEY = '__manage__'

type Props = {
  views: AgentBuilderDashboardView[]
  loading: boolean
  activeView: AgentBuilderDashboardView | null
  isModified: boolean
  /** Set while a DI admin impersonates a user: their views are shown read-only. */
  readOnlyEmail: string | null
  updating: boolean
  onSelect: (view: AgentBuilderDashboardView | null) => void
  onSaveNew: () => void
  onUpdate: () => void
  onManage: () => void
}

/** The "View" row under the dashboard title: pick, save and manage personal saved views. */
export function SavedViewsMenu({
  views,
  loading,
  activeView,
  isModified,
  readOnlyEmail,
  updating,
  onSelect,
  onSaveNew,
  onUpdate,
  onManage,
}: Props) {
  const readOnly = Boolean(readOnlyEmail)
  const atLimit = views.length >= MAX_SAVED_VIEWS_PER_DASHBOARD
  const sortedViews = [...views].sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }))

  function select(view: AgentBuilderDashboardView | null) {
    if (view?.viewId === activeView?.viewId && !isModified) return
    if (!isModified || !activeView) {
      onSelect(view)
      return
    }
    Modal.confirm({
      title: `Discard changes to "${activeView.name}"?`,
      content: 'Your unsaved changes to this view will be lost.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => onSelect(view),
    })
  }

  const viewItems: NonNullable<MenuProps['items']> = sortedViews.length > 0
    ? sortedViews.map((view) => ({
        key: view.viewId,
        label: (
          <span className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate">{view.name}</span>
            {view.isDefault ? <span className="text-[12px] text-gray-500">Default</span> : null}
            {view.isDefault ? <StarFilled className="!text-brand-500" /> : null}
            {view.viewId === activeView?.viewId ? <CheckOutlined className="text-gray-700" /> : null}
          </span>
        ),
      }))
    : [{
        key: '__empty__',
        disabled: true,
        label: (
          <span className="block max-w-[240px] whitespace-normal text-[12px] text-gray-500">
            {readOnly ? 'This user has no saved views.' : 'Save your filters, sort and drilldowns to come back to them in one click.'}
          </span>
        ),
      }]

  const items: MenuProps['items'] = [
    {
      key: DEFAULT_KEY,
      icon: <ReloadOutlined />,
      label: (
        <span className="flex flex-col leading-tight">
          <span className="flex items-center gap-2">
            Dashboard default
            {!activeView ? <CheckOutlined className="text-gray-700" /> : null}
          </span>
          <span className="text-[12px] text-gray-500">Filters and sorting from the dashboard design</span>
        </span>
      ),
    },
    { type: 'group', key: '__views__', label: readOnly ? 'Their views' : 'Your views', children: viewItems },
    ...(readOnly ? [] : [
      { type: 'divider' as const },
      {
        key: SAVE_KEY,
        icon: <PlusOutlined />,
        disabled: atLimit,
        label: atLimit ? `Save current view… (${MAX_SAVED_VIEWS_PER_DASHBOARD} max)` : 'Save current view…',
      },
      ...(activeView ? [{
        key: UPDATE_KEY,
        icon: <SaveOutlined />,
        disabled: !isModified || updating,
        label: `Update "${activeView.name}"`,
      }] : []),
      { key: MANAGE_KEY, icon: <SettingOutlined />, label: 'Manage views…' },
    ]),
  ]

  const handleClick: MenuProps['onClick'] = ({ key }) => {
    if (key === DEFAULT_KEY) return select(null)
    if (key === SAVE_KEY) return onSaveNew()
    if (key === UPDATE_KEY) return onUpdate()
    if (key === MANAGE_KEY) return onManage()
    const view = views.find((item) => item.viewId === key)
    if (view) select(view)
  }

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="saved-views-row">
      <span className="text-[12px] font-semibold uppercase tracking-[1.6px] text-gray-500">View</span>
      <Dropdown
        trigger={['click']}
        menu={{ items, onClick: handleClick, selectable: false }}
        overlayClassName="min-w-[280px]"
      >
        <Button className="ab-header-btn min-w-[220px] !justify-between" size="small" loading={loading}>
          <span className="flex min-w-0 items-center gap-2">
            {activeView?.isDefault ? <StarFilled className="!text-brand-500" /> : null}
            <span className="truncate">{activeView?.name ?? 'Dashboard default'}</span>
            {isModified ? <Tag color="gold" className="!m-0 !text-xs">Modified</Tag> : null}
          </span>
          <DownOutlined className="text-[10px]" />
        </Button>
      </Dropdown>
      <span className="text-[12px] text-gray-500">
        {readOnlyEmail
          ? `Viewing ${readOnlyEmail}'s saved views (read-only).`
          : 'Only you can see your saved views.'}
      </span>
    </div>
  )
}
